import React from 'react';
import { format } from 'date-fns';
import { shortenHash } from '@soundxyz/utils/const';
import { FollowButton } from '@components/basics/FollowButton';
import { Avatar } from '@components/basics/Images/Avatar';
import { ChannelMembersModal } from '@components/modals/Channels/ChannelMembersModal';
import { useModal } from '@context/modal';
import { ModalType } from '@context/modalType';
import { useStableCallback } from '@hooks/useStableCallback';
import { useChannelMembers } from '../useChannelMembers';
import { useChannelStats } from '../useChannelStats';

const CURATORS_LIMIT = 5;

function Skeleton() {
  return (
    <div className="flex w-full items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 flex-shrink-0 animate-pulse rounded-full bg-neutral200" />

        <div className="flex h-6 w-[120px] animate-pulse rounded-md bg-neutral200" />
      </div>
      <div>
        <div className="h-6 w-20 animate-pulse rounded-md bg-neutral200" />
      </div>
    </div>
  );
}

export function ChannelMembers({ channelId }: { channelId: string }) {
  const { openModal } = useModal();

  const { channelMembers, isLoading, hasNextPage } = useChannelMembers({
    channelId,
    limit: CURATORS_LIMIT,
  });

  const { memberCount } = useChannelStats({ channelId });

  const formatDate = useStableCallback((date: string) => format(new Date(date), 'MMM yyyy'));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="font-title text-title-s font-medium text-base800">Members</h2>

        {hasNextPage && (
          <button
            className="font-base text-[11px] font-medium leading-[100%] text-base500 transition-all duration-500 hover:brightness-[80%]"
            onClick={() => {
              openModal(ModalType.CHANNEL_MEMBERS, {
                body: <ChannelMembersModal channelId={channelId} membersNumber={memberCount} />,
              });
            }}
          >
            See all
          </button>
        )}
      </div>

      {isLoading ? (
        <>
          <Skeleton />
          <Skeleton />
          <Skeleton />
        </>
      ) : !channelMembers.length ? (
        <span className="font-base text-base-m font-normal text-base700">No channel members.</span>
      ) : (
        channelMembers.map(member => {
          return (
            <div key={member.id} className="flex w-full items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 flex-shrink-0">
                  <Avatar
                    size={40}
                    src={member.user.avatar?.url}
                    walletAddress={member.user.publicAddress}
                    webappUri={member.user.artist?.webappUri ?? member.user.webappUri}
                    borderRadius={999}
                  />
                </div>

                <div className="flex w-full flex-col gap-[2px]">
                  <span className="font-title text-[13px] font-medium leading-[120%] text-base600">
                    {member.user.artist?.name ??
                      member.user.displayName ??
                      shortenHash(member.user.publicAddress)}
                  </span>
                  <span className="font-base text-[11px] font-medium leading-[100%] text-base500">
                    Since {formatDate(member.createdAt)}
                  </span>
                </div>
              </div>
              <div>
                <FollowButton
                  artistId={member.user.artist?.id}
                  userId={member.user.id}
                  variant="primary"
                  sourceLocation="Channel Page"
                  size="XS"
                />
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
